import React, { createContext, useContext, useMemo, useState } from 'react';
import { useAuthContext } from 'services/authentication/AuthenticationContext';
import { useFetchData } from 'services/api/useFetchData';
import { Division } from '../../views/environments/EnvironmentTypes';

/*
 * SettingsDivisionContext.tsx
 * Holds the division that is selected in the settings menu. By default the
 * settings are shown for the whole environment. Users without an own division
 * can switch to a division, including the main division, to edit the division
 * scoped settings and see the division scoped lists. Users with an own division
 * are locked to that division. When the divisions feature is inactive the
 * environment behaviour applies.
 */

interface SettingsDivisionContextType {
    divisions: Division[];
    selectedDivision: Division | null;
    setSelectedDivisionId: (id: number | null) => void;
    isEnvironmentSelected: boolean;
    showDivisionSelector: boolean;
    refetchDivisions: () => void;
}

// Create the context with the environment behaviour as default
const SettingsDivisionContext = createContext<SettingsDivisionContextType>({
    divisions: [],
    selectedDivision: null,
    setSelectedDivisionId: () => {},
    isEnvironmentSelected: true,
    showDivisionSelector: false,
    refetchDivisions: () => {},
});

export const SettingsDivisionProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const { userDivision, activeFeatures } = useAuthContext();
    const [selectedDivisionId, setSelectedDivisionId] = useState<number | null>(null);

    // Only fetch the division list when the divisions feature is active
    const hasDivisionsFeature = activeFeatures.includes('divisions');
    const { response, refetch: refetchDivisions } = useFetchData({
        apiUrl: hasDivisionsFeature ? 'get_division_list' : '',
        params: useMemo(() => ({ is_active: true }), []),
    });
    const divisions: Division[] = response?.results ?? [];

    // Resolve the selected division based on the user division or the selection
    const selectedDivision = useMemo(() => {
        // Users with an own division are locked to that division
        // A user of the main division keeps the environment behaviour
        if (userDivision) {
            const lockedDivision = divisions.find(division => division.id === userDivision) || null;
            return lockedDivision && !lockedDivision.is_main ? lockedDivision : null;
        }

        // Otherwise use the selected division, no selection means the whole environment
        if (selectedDivisionId) {
            return divisions.find(division => division.id === selectedDivisionId) || null;
        }
        return null;
    }, [divisions, userDivision, selectedDivisionId]);

    // Determine whether the environment wide behaviour applies
    const isEnvironmentSelected = !selectedDivision;

    // Only show the selector for users without an own division and multiple divisions
    const showDivisionSelector = hasDivisionsFeature && !userDivision && divisions.length > 1;

    return (
        <SettingsDivisionContext.Provider value={{
            divisions,
            selectedDivision,
            setSelectedDivisionId,
            isEnvironmentSelected,
            showDivisionSelector,
            refetchDivisions,
        }}>
            {children}
        </SettingsDivisionContext.Provider>
    );
};

// Hook to use the settings division context
export const useSettingsDivision = () => useContext(SettingsDivisionContext);

export default SettingsDivisionContext;
